import { formatProductTitle, formatMoney } from './wishlist-utils.js';

const GUEST_WISHLIST_KEY = 'cb-wishlist';

function getGuestWishlist() {
  try {
    return JSON.parse(localStorage.getItem(GUEST_WISHLIST_KEY)) || [];
  } catch {
    return [];
  }
}

function setGuestWishlist(items) {
  localStorage.setItem(GUEST_WISHLIST_KEY, JSON.stringify(items));
}

class GuestWishlist extends HTMLElement {
  constructor() {
    super();
    this.controller = new AbortController();

    this.grid = null;
    this.emptyState = null;
    this.loadingState = null;
  }

  connectedCallback() {
    const signal = this.controller.signal;

    this.grid = this.querySelector('[data-guest-wishlist-grid]');
    this.emptyState = this.querySelector('[data-guest-wishlist-empty]');
    this.loadingState = this.querySelector('[data-guest-wishlist-loading]');

    this.removeLabel = this.getAttribute('data-remove-label') || 'Remove';
    this.viewLabel = this.getAttribute('data-view-label') || 'View product';
    this.coloursLabel = this.getAttribute('data-colours-label') || 'colours';

    this.render();

    // Re-sync if hearts are toggled elsewhere on the page
    document.addEventListener(
      'wishlist:updated',
      (e) => {
        if (e.detail?._source === 'guest-wishlist') return;
        this.render();
      },
      {
        signal,
      }
    );
  }

  disconnectedCallback() {
    this.controller.abort();
  }

  async render() {
    const items = getGuestWishlist().reverse();

    if (items.length === 0) {
      this.loadingState?.classList.add('hidden');
      this.grid?.classList.add('hidden');
      this.emptyState?.classList.remove('hidden');
      return;
    }

    this.emptyState?.classList.add('hidden');
    this.loadingState?.classList.remove('hidden');
    this.grid?.classList.add('hidden');

    const products = await this.fetchProducts(items);

    this.loadingState?.classList.add('hidden');

    if (products.length === 0) {
      this.emptyState?.classList.remove('hidden');
      return;
    }

    this.grid.innerHTML = '';
    for (const product of products) {
      this.grid.appendChild(this.createCard(product));
    }
    this.grid.classList.remove('hidden');
  }

  async fetchProducts(items) {
    const fetches = items
      .filter((item) => item.h)
      .map(async (item) => {
        try {
          const response = await fetch(`/products/${item.h}.js`);
          if (response.ok) {
            const product = await response.json();
            if (item.cc) product._colorCount = item.cc;
            if (item.v) product._videoUrl = item.v;
            return product;
          }
        } catch (e) {
          console.error(`Guest Wishlist: Error fetching product ${item.h}`, e);
        }
        return null;
      });
    const results = await Promise.allSettled(fetches);
    return results
      .filter((r) => r.status === 'fulfilled' && r.value)
      .map((r) => r.value);
  }

  createCard(product) {
    const card = document.createElement('div');
    card.setAttribute('data-guest-wishlist-item', '');
    card.setAttribute('data-product-id', product.id);
    card.classList.add('relative', 'flex', 'flex-col', 'gap-2');

    const imageUrl = product.featured_image
      ? product.featured_image.replace(/\.([a-z]+)(\?|$)/, '_600x.$1$2')
      : '';
    const videoUrl = product._videoUrl || '';

    const formattedTitle = formatProductTitle(product);

    const comparePrice =
      product.compare_at_price && product.compare_at_price > product.price
        ? `<p class="line-through">${formatMoney(product.compare_at_price)}</p>`
        : '';

    const coloursHtml = `<a href="/products/${product.handle}" class="text-body text-button-text-disabled no-underline ${product._colorCount > 0 ? '' : 'invisible'}">${product._colorCount} ${this.coloursLabel}</a>`;

    const hoverImageUrl =
      !videoUrl && product.images?.[1]
        ? product.images[1].replace(/\.([a-z]+)(\?|$)/, '_600x.$1$2')
        : '';

    card.innerHTML = `
      <div class="group/image relative w-full">
        <a href="/products/${product.handle}" class="flex flex-col gap-2 no-underline" aria-label="${formattedTitle}">
          <div class="w-full aspect-[2/3] h-fit">
            ${
              videoUrl
                ? `<video data-lazy-src="${videoUrl}" poster="${imageUrl}" autoplay loop muted playsinline class="w-full h-full object-cover"></video>`
                : imageUrl
                  ? `<img src="${imageUrl}" alt="${formattedTitle}" class="w-full h-full object-cover" loading="lazy" />`
                  : ''
            }
          </div>
          ${hoverImageUrl ? `<div class="w-full aspect-[2/3] !absolute inset-0 opacity-0 group-hover/image:opacity-100 transition-opacity duration-300"><img src="${hoverImageUrl}" alt="${formattedTitle}" class="w-full h-full object-cover" loading="lazy" /></div>` : ''}
        </a>
      </div>
      <div class="flex flex-col gap-[2px]">
        <div class="flex w-full items-start justify-between">
          <div class="text-body flex min-w-0 flex-1 flex-col gap-1">
            <a href="/products/${product.handle}" class="line-clamp-2 no-underline capitalize">${formattedTitle}</a>
            <a href="/products/${product.handle}" class="flex flex-row gap-2 no-underline">
              ${comparePrice}
              <p>${formatMoney(product.price)}</p>
            </a>
          </div>
        </div>
        ${coloursHtml}
        <div class="mt-3 flex flex-col gap-1">
          <button
            type="button"
            class="text-body cursor-pointer text-left underline"
            data-guest-remove
            data-product-id="${product.id}"
            data-product-handle="${product.handle}"
          >${this.removeLabel}</button>
          <button
          type='button'
          class='text-body cursor-pointer text-left underline'
          data-product-url='/products/${product.handle}'
          onclick="sessionStorage.setItem('open-size-selector', 'true'); window.location.href = '/products/${product.handle}';"
        >
          ${this.viewLabel}
        </button>
        </div>
      </div>
    `;

    const removeBtn = card.querySelector('[data-guest-remove]');
    removeBtn?.addEventListener(
      'click',
      () => this.handleRemove(product.id, card),
      {
        signal: this.controller.signal,
      }
    );

    return card;
  }

  handleRemove(productId, cardEl) {
    const items = getGuestWishlist();
    const filtered = items.filter(
      (item) => String(item.id) !== String(productId)
    );
    setGuestWishlist(filtered);

    cardEl.remove();

    document.dispatchEvent(
      new CustomEvent('wishlist:updated', {
        detail: { productId, isWishlisted: false, _source: 'guest-wishlist' },
      })
    );

    // Check if grid is now empty
    const remaining = this.grid?.querySelectorAll('[data-guest-wishlist-item]');
    if (!remaining || remaining.length === 0) {
      this.grid?.classList.add('hidden');
      this.emptyState?.classList.remove('hidden');
    }
  }
}

if (!customElements.get('guest-wishlist')) {
  customElements.define('guest-wishlist', GuestWishlist);
}
